import DomComponent from '../../lib/domComponent';
import { each } from '../../lib/util';

/**
 * Modal Component handles all the modals of the site
 */
export default class ModalComponent extends DomComponent {
    // The modal selector
    static selector = '.modal';

    /**
     * The Modal Component constructor
     *
     * @param {object} el The selector element
     */
    constructor(el) {
        super(el);
        // Cache the DOM
        this.cacheDom();
        // Add event listeners
        this.addEventHandlers();
    }

    /**
     * Cache the DOM
     */
    cacheDom() {
        // List of all the modal triggers
        this.modalTriggers = document.querySelectorAll('[data-modal-target]');
        // The close buttons for the modal
        this.closeButtons = this.el.querySelectorAll('.modal__close-button');
        // The overlay container
        this.overlay = this.el.querySelector('.modal__document');
        // List of all modal contents
        this.modalContent = document.querySelectorAll('.modal__content');
        //iframe video player
        this.video = document.querySelector("[data-modal-video]");
    }

    /**
     * Handle all the event listeners of the component
     */
    addEventHandlers() {
        // Close the modal when a user clicks on the "Close" button
        each(this.closeButtons, (trigger) => {
            trigger.addEventListener('click', () => {
                // Close the Modal
                this.closeModal();
            });
        });
        // Close the modal when a user clicks on the overlay (The overlay is the ".modal__document" element)
        this.el.addEventListener('click', this.overlayCloseModal.bind(this));
        // Close the modal when a user clicks on the "Escape" key
        document.addEventListener('keyup', this.handleEscape.bind(this));
        // Handle all the Modal Triggers
        this.handleTriggers();
    }

    /**
     * Select the correct modal content from the trigger
     *
     * @param {string} modalId The modal id to display
     */
    selectModalContent(modalId, videoId) {
        // Hide all the content of all modals
        each(this.modalContent, (content) => {
            content.style.display = 'none';
        });
        // Set the active modal content so we can trap the focus on the content only. This will be used by the trapFocus function
        this.activeModalContent = this.el.querySelector(`[data-modal-id="${modalId}"]`);
        // Display the correct content of the modal
        this.activeModalContent.style.display = 'block';

        if(videoId != null){
            this.prepareVideo(videoId);
        }
    }

    /**
     * Handle video modal player
     *
     * @param {string} videoId The YouTube or Youku video ID.
     */
    prepareVideo(videoId) {
        switch(window.settings.videoPlatform) {
            case 'Youku':
                this.video.setAttribute(
                    "src",
                    `https://player.youku.com/embed/${videoId}`
                );
                break;
            default: // YouTube.
                this.video.setAttribute(
                    "src",
                    `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`
                );
                break;
        }

        this.el.classList.add("modal--video");
    }

    /**
     * Handle all the triggers like the
     */
    handleTriggers() {
        // Loop through all the modal triggers and add an event listener
        each(this.modalTriggers, (trigger, index) => {
            // Add event listener to modal trigger
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                const videoId = trigger.dataset.videoId;
                // Select the correct content for the modal
                this.selectModalContent(trigger.dataset.modalTarget, videoId);
                // Open the modal
                this.openModal();
                // Set the active trigger in order to set the focus back when the modal closes
                this.activeTrigger = this.modalTriggers[index];
            });
        });
    }

    /**
     * Trap the focus on the model for users using screen readers
     */
    trapFocus() {
        // Get a list of focusable elements
        let focusableEls = this.activeModalContent.querySelectorAll('a, button, textarea, input, select, iframe, [tabindex]'),
            // First focusable element
            firstFocusableEl = focusableEls[0],
            // Last focusable element
            lastFocusableEl = focusableEls[focusableEls.length - 1];

        // Add an event listener for the "Tab" key
        this.el.addEventListener('keydown', function (e) {
            let isTabPressed = (e.key === 'Tab' || e.keyCode === 9);
            // Did the user click on the "Tab" key
            if (isTabPressed) {
                // The user clicked on "Shift + Tab"
                if (e.shiftKey) {
                    // Is the current active element the first focusable element
                    if (document.activeElement === firstFocusableEl) {
                        // Set focus to the last focusable element
                        lastFocusableEl.focus();
                        e.preventDefault();
                    }
                }
                // The user clicked on "Tab"
                else {
                    if (document.activeElement === lastFocusableEl) {
                        // Set focus to the first focusable element
                        firstFocusableEl.focus();
                        e.preventDefault();
                    }
                }
            }
        });
    }

    /**
     * Open the modal. Display the modal on the page
     */
    openModal() {
        // Display the modal
        this.el.style.display = 'block';
        // Show modal to screen readers
        this.el.setAttribute('aria-hidden', 'false');
        // Set focus to modal
        this.el.focus();
        // Set a trap focus so the user stays within the modal when open
        this.trapFocus();
        // Add an "open" class to apply a fade in
        this.el.classList.add('open');
    }

    /**
     * Close the modal. Hide the modal
     */
    closeModal() {
        // Remove the "open" class to fade out the modal
        this.el.classList.remove('open');
        // Hide modal to screen readers
        this.el.setAttribute('aria-hidden', 'true');
        // Set the focus back to the element that triggered the modal
        if (this.activeTrigger !== undefined) {
            this.activeTrigger.focus();
        }
        // Wait until the fade out transition finishes before hiding the modal
        setTimeout(() => {
            // Hide the modal
            this.el.style.display = 'none';
            this.el.classList.remove('modal--video');
            if(this.video){
                this.video.setAttribute('src', '');
            }
        }, 400);

    }

    /**
     * Close the modal when a user clicks on the overlay
     *
     * @param {object} e The click event
     */
    overlayCloseModal(e) {
        // Check to make sure that the overlay was clicked
        if (e.target === this.overlay || e.target === this.el) {
            // Close the modal if the overlay was clicked
            this.closeModal();
        }
    }

    /**
     * Close the modal when a user clicks on the "Escape" button
     *
     * @param {object} e The click event
     */
    handleEscape(e) {
        // Check to make sure that the "Escape" button was clicked (27 is the code for the Escape button)
        if (e.which === 27) {
            // Close the modal
            this.closeModal();
        }
    }
}
